/**
 * Legacy Mageworx APO (Product Options generation) cart sync.
 *
 * The legacy app adds each option surcharge as a separate HIDDEN cart line
 * (product_type 'mw_product_option' / 'mw_product_option_cloned', or
 * properties._mw_is_variant), linked to its parent line by
 * properties._mw_option_relation. The old theme kept those children in step
 * by force-resubmitting the whole cart form via jQuery; Horizon's cart is
 * AJAX-driven, so this module reconciles instead:
 *
 *   - parent quantity changed  -> scale each linked child proportionally
 *   - parent removed           -> remove its orphaned children
 *
 * Loaded only when settings.apo_generation == 'legacy' (from
 * snippets/apo-legacy-objects.liquid). Listens for Horizon's 'cart:update'
 * event, applies /cart/change.js ops sequentially, then dispatches its own
 * 'cart:update' (source 'apo-legacy-sync', no sections payload) so cart
 * components re-render themselves.
 *
 * On load it always fetches /cart.js to refresh its snapshot and self-heals
 * any orphaned child lines left behind by an interrupted reconciliation
 * (orphan removal only — quantity scaling never runs at load, because the
 * 1:1 fallback could mis-scale non-1:1 children without a prior snapshot).
 */
(function () {
  'use strict';

  var RELATION_KEY = '_mw_option_relation';
  var SYNC_SOURCE = 'apo-legacy-sync';

  /**
   * True when a /cart.js line is a legacy APO child (hidden surcharge) line.
   * @param {Object} line - a /cart.js item
   * @returns {boolean}
   */
  function isChildLine(line) {
    var type = line.product_type || '';
    if (type === 'mw_product_option' || type === 'mw_product_option_cloned') return true;
    return Boolean(line.properties && line.properties._mw_is_variant);
  }

  /**
   * The line's relation token, or null when it has none.
   * @param {Object} line - a /cart.js item
   * @returns {string|null}
   */
  function relationOf(line) {
    return (line.properties && line.properties[RELATION_KEY]) || null;
  }

  /**
   * Computes the /cart/change.js operations that bring child lines back in
   * step with their parents after a cart change.
   *
   * The per-parent-unit ratio comes from the previous cart snapshot (how many
   * child units each parent unit carried before the change); without a
   * snapshot it defaults to 1:1, which matches how the legacy app adds lines.
   *
   * @param {Object|null} prevCart - /cart.js JSON before the change (null = unknown)
   * @param {Object} nextCart - /cart.js JSON after the change
   * @returns {Array<{id: string, quantity: number}>} ops keyed by line key
   */
  function computeReconciliation(prevCart, nextCart) {
    var ops = [];
    var next = (nextCart && nextCart.items) || [];
    var prev = (prevCart && prevCart.items) || [];

    var parents = {};
    next.forEach(function (line) {
      var rel = relationOf(line);
      if (rel && !isChildLine(line)) parents[rel] = line;
    });

    var prevParents = {};
    var prevChildren = {};
    prev.forEach(function (line) {
      var rel = relationOf(line);
      if (!rel) return;
      if (isChildLine(line)) {
        (prevChildren[rel] = prevChildren[rel] || {})[line.key] = line;
      } else {
        prevParents[rel] = line;
      }
    });

    next.forEach(function (line) {
      if (!isChildLine(line)) return;
      var rel = relationOf(line);
      if (!rel) return;

      var parent = parents[rel];
      if (!parent) {
        // Parent gone — remove the orphaned child.
        ops.push({ id: line.key, quantity: 0 });
        return;
      }

      var ratio = 1;
      var prevParent = prevParents[rel];
      var prevChild = prevChildren[rel] && prevChildren[rel][line.key];
      if (prevParent && prevChild && prevParent.quantity > 0) {
        ratio = prevChild.quantity / prevParent.quantity;
      }

      var want = Math.max(0, Math.round(parent.quantity * ratio));
      if (want !== line.quantity) ops.push({ id: line.key, quantity: want });
    });

    return ops;
  }

  /* -------------------------------------------------------------------- */
  /* Browser wiring                                                        */
  /* -------------------------------------------------------------------- */

  function initBrowser() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    // Seed the snapshot from the Liquid data bridge when it rendered on this page.
    var lastCart = (window.mwProductOptionsObjects && window.mwProductOptionsObjects.cart) || null;
    var syncing = false;

    function fetchCart() {
      return fetch(root + 'cart.js').then(function (r) {
        if (!r.ok) throw new Error('cart.js fetch failed: ' + r.status);
        return r.json();
      });
    }

    // Sequential on purpose: concurrent /cart/change.js calls race each other.
    function applyOps(ops) {
      return ops.reduce(function (chain, op) {
        return chain.then(function () {
          return fetch(root + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(op),
          }).then(function (r) {
            if (!r.ok) throw new Error('cart/change.js failed: ' + r.status);
            return r.json();
          });
        });
      }, Promise.resolve());
    }

    function dispatchSyncUpdate(fresh) {
      // No `sections` payload: Horizon's cart components fall back to
      // re-rendering their own section (see component-cart-items.js).
      // itemCount keeps the header cart bubble honest (cart-icon.js reads it);
      // item_count includes hidden child lines, matching the server-rendered
      // bubble.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          detail: {
            resource: fresh,
            sourceId: SYNC_SOURCE,
            data: { source: SYNC_SOURCE, itemCount: fresh.item_count },
          },
        })
      );
    }

    document.addEventListener('cart:update', function (event) {
      if (syncing) return; // updates triggered by our own reconciliation
      var source = event && event.detail && event.detail.data && event.detail.data.source;
      if (source === SYNC_SOURCE) return;

      fetchCart()
        .then(function (cart) {
          var ops = computeReconciliation(lastCart, cart);
          if (ops.length === 0) {
            lastCart = cart;
            return;
          }
          syncing = true;
          return applyOps(ops)
            .then(fetchCart)
            .then(function (fresh) {
              lastCart = fresh;
              dispatchSyncUpdate(fresh);
            })
            .finally(function () {
              syncing = false;
            });
        })
        .catch(function (err) {
          console.error('APO legacy cart sync failed:', err);
        });
    });

    // Load-time pass: always refresh the snapshot and self-heal any orphaned
    // child lines left behind by an interrupted reconciliation (navigation
    // mid-ops, network error). Orphan removal only — quantity-scaling ops
    // must not run here, because the 1:1 fallback could mis-scale non-1:1
    // children without a trustworthy previous snapshot.
    fetchCart()
      .then(function (cart) {
        lastCart = cart;
        var ops = computeReconciliation(null, cart).filter(function (op) {
          return op.quantity === 0;
        });
        if (ops.length === 0) return;
        syncing = true;
        return applyOps(ops)
          .then(fetchCart)
          .then(function (fresh) {
            lastCart = fresh;
            dispatchSyncUpdate(fresh);
          })
          .catch(function (err) {
            console.error('APO legacy cart self-heal failed:', err);
          })
          .finally(function () {
            syncing = false;
          });
      })
      .catch(function () {});
  }

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    initBrowser();
  }

  // Node (unit tests) — same pattern as assets/apo-image-swap.js.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      computeReconciliation: computeReconciliation,
      isChildLine: isChildLine,
      relationOf: relationOf,
    };
  }
})();
